import React, { useEffect } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, Skeleton, Typography } from '@mui/material';
import { format, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useLazyGetTransactionForUserQuery } from '../../apis/konektApi';

interface StaffActivityFeedProps {
  userId: string;
  userName: string;
  lastLogin?: string;
}

const StaffActivityFeed: React.FC<StaffActivityFeedProps> = ({ userId, userName, lastLogin }) => {
  const { t } = useTranslation();

  const [fetchTodayTransactions, todayResult] = useLazyGetTransactionForUserQuery();

  useEffect(() => {
    fetchTodayTransactions({ userId, date: startOfDay(new Date()).toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const todayIncomeTotal =
    todayResult.data?.incomes.reduce((sum, row) => sum + row.fee_amount, 0) ?? 0;
  const todayTransactionCount =
    (todayResult.data?.incomes.length ?? 0) + (todayResult.data?.expenses.length ?? 0);

  const isLoading = todayResult.isLoading;

  const formattedLastLogin = lastLogin ? format(new Date(lastLogin), 'dd/MM/yyyy HH:mm') : '—';

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader title={t('staff.activity_feed_title')} />
      <CardContent>
        {isLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Skeleton variant="rectangular" height={80} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box p={2} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="subtitle2" color="text.secondary">
                  {t('staff.transactions_today')}
                </Typography>
                <Typography variant="h4" fontWeight={600} mt={0.5}>
                  {todayTransactionCount}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box p={2} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="subtitle2" color="text.secondary">
                  {t('staff.income_collected_today')}
                </Typography>
                <Typography variant="h4" fontWeight={600} mt={0.5} color="success.main">
                  ${todayIncomeTotal.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box p={2} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="subtitle2" color="text.secondary">
                  {t('staff.last_login')}
                </Typography>
                <Typography variant="body2" mt={0.5} color="text.secondary">
                  {formattedLastLogin}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffActivityFeed;
